import React from 'react'

import { Skeleton, Spinner, useLocale } from '@rewardops/ui'

import { useApi } from '~/components/hooks/use-api'
import { useElpPromotions } from '~/components/hooks/use-elp-promotions'
import { useTranslation } from '~/app/i18n/client'
import { PAWSectionType } from './paw-section-types'

const PromotionCard = ({ promotion }: { promotion: any }) => {
  const { locale } = useLocale()
  return (
    <div
      key={promotion?.instanceIds}
      className="min-h-80 w-full overflow-hidden rounded-xl border bg-white text-card-foreground shadow-sm md:w-[240px]"
    >
      <div className="h-20 w-full overflow-hidden">
        <img
          src={promotion.promoImage?.file?.url}
          className="h-full w-full object-cover"
        />
      </div>
      <div className="p-5">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 text-sm font-semibold leading-snug">
            {promotion.title}
          </div>
          <div className="flex-shrink-0 whitespace-nowrap pt-0.5 text-xs text-muted-foreground">
            Ends{' '}
            {new Date(promotion?.endDate)?.toLocaleDateString(locale, {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
              timeZone: 'UTC',
            })}
          </div>
        </div>
        <div className="mt-3 border-t border-border pt-3">
          <div className="max-h-32 overflow-y-auto text-xs text-muted-foreground">
            {promotion?.description ?? promotion?.promotionDescription}
          </div>
        </div>
      </div>
    </div>
  )
}

const ElpPAWPromotionCards = ({ content }: { content: any }) => {
  const { api } = useApi()
  const { t: translate } = useTranslation()

  const {
    data: elpOffers,
    isLoading: elpOffersLoading,
    refetch: refetchOffers,
    isRefetching,
  } = api.elpPromotions.getPromotions.useQuery(null)

  const isLoading = elpOffersLoading || isRefetching

  const { promotions } = useElpPromotions({
    elpOffers: elpOffers?.offers,
    promotionsContent: content?.promotions,
    offersLoading: isLoading,
  })

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {isLoading ? (
        Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-[250px] w-full" />
        ))
      ) : promotions?.length ? (
        promotions?.map((promotion: any) => (
          <React.Fragment>
            <PromotionCard promotion={promotion} />
          </React.Fragment>
        ))
      ) : (
        <div>{translate('elpPromotions.noOffers')}</div>
      )}
    </div>
  )
}

export const Promotions: React.FC<PAWSectionType> = ({
  content,
  data,
}: PAWSectionType) => {
  const { api } = useApi()

  const { locale } = useLocale()

  const { data: promotionsContent, isLoading } =
    api.contentful.getById.useQuery({
      key: 'paw-dashboard-promotions',
      language: locale,
    })

  return (
    <section id={content?.sectionId} data-testid={content?.sectionId}>
      <h3 className="mb-6 text-xl font-semibold">{content?.title}</h3>
      {isLoading ? (
        <Spinner />
      ) : promotionsContent ? (
        <ElpPAWPromotionCards content={promotionsContent} />
      ) : null}
    </section>
  )
}
